import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Spinner } from "reactstrap";
import {
  IoClose,
  IoPrintOutline,
  IoCreateOutline,
  IoShareSocialOutline,
  IoCardOutline,
  IoCopyOutline,
  IoTrashOutline,
  IoCheckmarkDoneCircleOutline
} from "react-icons/io5";
import { TfiReload } from "react-icons/tfi";
import { get, post } from "../../../../../utils/helpers/api";
import { toast } from "react-toastify";
import DefaultButton from "../../../../../components/Buttons/Default";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import DOC_STYLES from "../../../utils/docStyles";
import { AWS_S3_URL, BASE_URL } from "../../../../../utils/constants";
import ShareConfirmationModal from "../../Quotes/Modals/ShareConfirmation";
import { useTranslation } from "react-i18next";
import LegalIdentifierLines from "../../../../../components/Documents/LegalIdentifierLines";
import { amountInWords, amountInWordsPrefix } from "../../../../../utils/helpers/numberToWords";
import { useSettings } from "../../../../../context/settings";
import { printSlug } from "../../../../../utils/helpers/text";
import DefaultDrawer from "../../../../../components/Drawers/Default";

const PRIMARY = "#6C63FF";

function fmt(n) {
  return parseFloat(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ViewDrawer({ invoiceId, isOpen, onClose, onDelete, refreshInvoices }) {
  const { t, i18n } = useTranslation();
  const { formatDate } = useSettings();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [shareConfirmationModalOpen, setShareConfirmationModalOpen] = useState(false);

  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const getInvoice = async () => {
    if (!invoiceId) return;

    try {
      const data = await get(`/sales/invoices/view/${invoiceId}`);
      setInvoice(data.invoice ?? null);
      setCompanyData(data.company ?? null);
    } catch (error) {
      toast.error(t('sales.failedLoadInvoice'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!invoiceId) return;
    setLoading(true);
    setInvoice(null);
    getInvoice();
  }, [invoiceId]);

  const currency = (invoice?.currency ?? "usd").toUpperCase();

  const customerName = invoice?.customer
    ? (invoice.customer.company_name || `${invoice.customer.firstname ?? ""} ${invoice.customer.lastname ?? ""}`.trim())
    : "—";

  const salespersonName = invoice?.salesperson
    ? `${invoice.salesperson.firstname ?? ""} ${invoice.salesperson.lastname ?? ""}`.trim()
    : null;

  const handlePrint   = () => window.print();
  const handleEdit    = () => { onClose(); navigate(`/sales/invoices/edit/${invoice.id}`, { state: { invoiceData: { ...invoice } } }); };

  const markAsSent = async () => {
    const data = await post(`/sales/invoices/mark-as-sent/${invoice.id}`);
    if (data?.success) {
      getInvoice();
      toast.success(t('sales.invoiceMarkedSent'));
    } else {
      toast.error(data?.message || t('sales.failedSentInvoice'));
    }
  };

  const markRecurring = async () => {
    const data = await post(`/sales/invoices/mark-recurring/${invoice.id}`);
    if (data?.success) {
      navigate(`/sales/recurring-invoices/edit/${data?.recurring_invoice?.id}`, { state: { invoiceData: { ...data?.recurring_invoice } } });
      toast.success(t('sales.invoiceMarkedRecurring'));
    } else {
      toast.error(data?.message || t('sales.failedRecurring'));
    }
  }

  const clone = async () => {
    const data = await post(`/sales/invoices/clone/${invoice.id}`);
    if (data?.success) {
      toast.success(t('sales.invoiceCloned'));
      navigate(`/sales/invoices/edit/${data.invoice.id}`, { state: { invoiceData: { ...data.invoice } } });
    } else {
      toast.error(data?.message || t('sales.failedCloneInvoice'));
    }
  };

  const invoiceLabel = invoice ? `${t('sales.invoiceHash')}${invoice.invoice_number || invoice.id}` : t('sales.invoiceDetails');

  const handleShare = async () => {
    const data = await post(`/sales/invoices/share/${invoice.id}`);
    if (data?.success) {
      toast.success(t('sales.invoiceSharedCopied'));
      navigator.clipboard.writeText(BASE_URL + '/invoice/' + invoice?.id);
      refreshInvoices();
    } else {
      toast.error(data?.message || t('sales.failedShareInvoice'));
    }
  }

  return (
    <DefaultDrawer 
      isOpen={isOpen} 
      size={680} 
      toggle={onClose}
    >
      {loading ?
        <Spinner color="primary" className="d-block mx-auto my-4" />
      :
        <div style={DOC_STYLES.wrap}>
          {/* ── Header ── */}
          <div style={DOC_STYLES.header}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" }}
                onClick={onClose}
                onMouseEnter={e => { e.currentTarget.style.background = "#f0f0f0"; e.currentTarget.style.color = "#333"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#888"; }}
              >
                <IoClose size={20} />
              </button>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#1d1d1f" }}>{invoiceLabel}</span>
              {invoice && 
                <Badge color="primary">{ invoice.status }</Badge>
              }
            </div>
          </div>

          {/* ── Action Bar ── */}
          <div style={DOC_STYLES.actionBar}>
            <DefaultButton className="btn-action" size="sm" onClick={handleEdit} disabled={!invoice} color="light" outline>
              <IoCreateOutline size={15} />
              {t('common.edit')}
            </DefaultButton>
            <DefaultButton className="btn-action" size="sm" onClick={() => setShareConfirmationModalOpen(true)} disabled={!invoice} color="light" outline>
              <IoShareSocialOutline size={15} />
              {t('common.share')}
            </DefaultButton>
            <DefaultButton className="btn-action" size="sm" onClick={handlePrint} disabled={!invoice} color="light" outline>
              <IoPrintOutline size={15} />
              {t('common.download')}
            </DefaultButton>
            {invoice?.status !== "paid" &&
              <DefaultButton className="btn-action" size="sm" onClick={() => navigate(`/sales/payments/create?invoice_id=${invoice.id}`)} disabled={!invoice} color="light" outline>
                <IoCardOutline size={15} />
                {t('sales.newPayment')}
              </DefaultButton>
            }
            <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down" size="sm">
              <DropdownToggle outline color="light" className="btn-action">
                {t('common.options')} <PiDotsThreeOutlineVerticalFill />
              </DropdownToggle>
              <DropdownMenu end>
                <DropdownItem onClick={markAsSent} disabled={!invoice || invoice.status === "sent"}>
                  <IoCheckmarkDoneCircleOutline size={16} /> {t('common.sent')}
                </DropdownItem>
                <DropdownItem onClick={markRecurring} disabled={!invoice}>
                  <TfiReload size={16} /> {t('sales.recurringInvoices')}
                </DropdownItem>
                <DropdownItem onClick={clone} disabled={!invoice}>
                  <IoCopyOutline size={16} /> {t('sales.clone')}
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem className="text-danger" onClick={onDelete} disabled={!invoice}>
                  <IoTrashOutline size={16} /> {t('common.delete')}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>

          {/* ── Scrollable document area ── */}
          <div style={DOC_STYLES.scroll}>
            {!loading && invoice && (
              <div id="quote-print-area" style={DOC_STYLES.paper}>

                {/* Company + Invoice Title */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 36 }}>
                  <div>
                    <img
                      src={companyData?.logo_path ? AWS_S3_URL + companyData.logo_path : require('../../../../../assets/images/brand.svg').default}
                      height={38}
                      style={{ marginBottom: 10 }}
                      alt={t('common.logo')}
                    />
                    <div style={{ fontWeight: 800, fontSize: 18, color: "#1d1d1f", lineHeight: 1.2 }}>
                      { companyData?.legal_name || t('common.companyNamePlaceholder') }
                    </div>
                    <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                      { companyData?.street_address || t('sales.addressPlaceholder') }<br />
                      { companyData?.city || t('common.city') } { companyData?.state || t('common.state') } { companyData?.postal_code || t('common.postalCode') }<br />
                      { printSlug(companyData?.country) || t('common.country') }<br />
                      { companyData?.email || t('common.emailPlaceholder') }
                    </div>
                    <LegalIdentifierLines party={companyData} compact />
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 800, fontSize: 30, color: PRIMARY, letterSpacing: 3, lineHeight: 1, textTransform: "uppercase" }}>
                      {t('sales.invoice')}
                    </div>
                    <div style={{ color: "#888", fontSize: 14, marginTop: 6, fontWeight: 600 }}>
                      #{invoice.invoice_number || invoice.id}
                    </div>
                  </div>
                </div>

                <hr style={DOC_STYLES.divider} />

                {/* Bill To + Meta */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
                  <div>
                    <div style={DOC_STYLES.label}>{t('sales.billTo')}</div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#1d1d1f" }}>{customerName}</div>
                    {invoice?.customer?.addresses?.length > 0 && 
                      <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                        { invoice.customer.addresses[0].street }<br />
                        { invoice.customer.addresses[0].city }, { invoice.customer.addresses[0].state } { invoice.customer.addresses[0].postal_code }<br />
                        { printSlug(invoice.customer.addresses[0].country) }
                      </div>
                    }
                    <LegalIdentifierLines party={invoice?.customer} compact />
                  </div>
                  <div>
                    <table style={DOC_STYLES.metaTable}>
                      <tbody>
                        <tr>
                          <td style={DOC_STYLES.metaThTd}>{t('sales.invoiceDate')}</td>
                          <td style={DOC_STYLES.metaTd}>{formatDate(invoice.invoice_date) || "—"}</td>
                        </tr>
                        <tr>
                          <td style={DOC_STYLES.metaThTd}>{t('sales.dueDate')}</td>
                          <td style={DOC_STYLES.metaTd}>{formatDate(invoice.due_date) || "—"}</td>
                        </tr>
                        {salespersonName && (
                          <tr>
                            <td style={DOC_STYLES.metaThTd}>{t('sales.salesperson')}</td>
                            <td style={DOC_STYLES.metaTd}>{salespersonName}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {invoice.subject && (
                  <div style={{ marginBottom: 24, fontSize: 13, background: "#f9f9ff", border: "1px solid #ede9ff", borderRadius: 7, padding: "10px 14px", color: "#444" }}>
                    <strong style={{ color: PRIMARY }}>{t('sales.re')}</strong>&nbsp;{invoice.subject}
                  </div>
                )}

                {/* Items table */}
                <table style={DOC_STYLES.itemsTable}>
                  <thead>
                    <tr>
                      <th style={{ ...DOC_STYLES.th, width: 36, borderRadius: "6px 0 0 0" }}>#</th>
                      <th style={DOC_STYLES.th}>{t('common.itemDescription')}</th>
                      <th style={{ ...DOC_STYLES.thRight, width: 60 }}>{t('common.qty')}</th>
                      <th style={{ ...DOC_STYLES.thRight, width: 100 }}>{t('common.unitPrice')}</th>
                      <th style={{ ...DOC_STYLES.thRight, width: 100 }}>{t('sales.taxPercent')}</th>
                      <th style={{ ...DOC_STYLES.thRight, width: 100 }}>{t('sales.discount')}</th>
                      <th style={{ ...DOC_STYLES.thRight, width: 100, borderRadius: "0 6px 0 0" }}>{t('common.total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ ...DOC_STYLES.td, textAlign: "center", color: "#aaa", padding: "32px 0" }}>{t('common.noItemsAdded')}</td>
                      </tr>
                    ) : (
                      invoice.items.map((item, i) => (
                        <tr key={item.id ?? i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                          <td style={DOC_STYLES.td}>{i + 1}</td>
                          <td style={DOC_STYLES.td}>
                            <span style={{ fontWeight: 600 }}>{item.product_name || "—"}</span>
                            {item.description && (
                              <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>{item.description}</div>
                            )}
                          </td>
                          <td style={DOC_STYLES.tdRight}>{item.quantity}</td>
                          <td style={DOC_STYLES.tdRight}>{fmt(item.unit_price)}</td>
                          <td style={DOC_STYLES.tdRight}>{fmt(item.tax_rate)}%</td>
                          <td style={DOC_STYLES.tdRight}>{fmt(item.discount_amount)}%</td>
                          <td style={DOC_STYLES.tdRight}>{fmt(item.total_price)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* Totals */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                  <div style={DOC_STYLES.totalsBox}>
                    <div style={DOC_STYLES.totalsLine}>
                      <span>{t('common.subTotal')}</span>
                      <span style={{ fontWeight: 500 }}>{fmt(invoice?.sub_total ?? 0)} {currency}</span>
                    </div>
                    {invoice.discount_amount > 0 && (
                      <div style={DOC_STYLES.totalsLine}>
                        <span>{t('sales.discount')}</span>
                        <span style={{ fontWeight: 500, color: "red" }}>- {fmt(invoice?.discount_amount ?? 0)} {currency}</span>
                      </div>
                    )}
                    {invoice.tax_amount > 0 && (
                      <div style={DOC_STYLES.totalsLine}>
                        <span>{t('sales.taxWithRate', { rate: invoice.tax_rate ?? 0 })}</span>
                        <span style={{ fontWeight: 500 }}>{fmt(invoice?.tax_amount ?? 0)} {currency}</span>
                      </div>
                    )}
                    {invoice.stamp_duty > 0 && (
                      <div style={DOC_STYLES.totalsLine}>
                        <span>{t('legal.stampDuty')}</span>
                        <span style={{ fontWeight: 500 }}>{fmt(invoice?.stamp_duty ?? 0)} {currency}</span>
                      </div>
                    )}
                    <div style={DOC_STYLES.totalsBold}>
                      <span>{t('common.total')}</span>
                      <span style={{ color: PRIMARY }}>{fmt(invoice?.total_amount ?? 0)} {currency}</span>
                    </div>
                  </div>
                </div>

                {/* Amount in words — legally required on an Algerian invoice */}
                <div style={{ marginTop: 20, paddingTop: 14, borderTop: "1px dashed #ddd", fontSize: 12.5, color: "#444" }}>
                  <span style={{ color: "#888" }}>{amountInWordsPrefix(i18n.language)} </span>
                  <span style={{ fontWeight: 600, textTransform: "capitalize" }}>
                    {amountInWords(invoice?.total_amount ?? 0, i18n.language, invoice?.currency)}
                  </span>
                </div>

                {/* Notes */}
                {invoice.notes && (
                  <div style={{ marginTop: 36, paddingTop: 20, borderTop: "1px solid #eee" }}>
                    <div style={DOC_STYLES.label}>{t('sales.notes')}</div>
                    <p style={{ fontSize: 13, color: "#555", whiteSpace: "pre-wrap", margin: 0, lineHeight: 1.7 }}>
                      {invoice.notes}
                    </p>
                  </div>
                )}

              </div>
            )}
          </div>
        </div>
      }

      <ShareConfirmationModal 
        isOpen={shareConfirmationModalOpen}
        toggle={() => setShareConfirmationModalOpen(!shareConfirmationModalOpen)}
        onConfirm={() => handleShare()}
      />
    </DefaultDrawer>
  );
}
